import React from 'react';
import { Bar } from 'react-chartjs-2';
import '../lib/charts';
import { SERIES } from '../lib/charts';
import useReport from '../lib/useReport';
import { useFilters } from '../contexts/FilterContext';
import { money, int, niceDate } from '../lib/format';
import { Card, Spinner, ErrorBox, DataTable, Empty, KpiCard } from '../components/ui';

// Productivity: how much revenue each labor hour produces, per location.
// Hours come from POS shift punches (employee_shift); shifts without a valid
// clock-out or with corrupt times are excluded.
export default function LaborPage() {
  const { reportParams, startDate, endDate } = useFilters();
  const { data, loading, error } = useReport('locations-performance', reportParams);

  if (error) return <ErrorBox message={error} />;
  if (loading || !data) return <Spinner label="Adding up shift hours…" />;

  const withLabor = data.filter(r => r.laborHours > 0);
  if (withLabor.length === 0) {
    return <Empty>No shift punches recorded for this period.</Empty>;
  }

  const totalHours = withLabor.reduce((a, r) => a + r.laborHours, 0);
  const totalSales = withLabor.reduce((a, r) => a + r.netSales, 0);
  const totalShifts = withLabor.reduce((a, r) => a + (r.shifts || 0), 0);
  const fleetSplh = totalHours > 0 ? Math.round(totalSales / totalHours) : 0;

  const sorted = [...withLabor].sort((a, b) => b.salesPerLaborHour - a.salesPerLaborHour);
  const barData = {
    labels: sorted.map(r => r.locationName),
    datasets: [{
      label: 'Net sales per labor hour',
      data: sorted.map(r => r.salesPerLaborHour),
      backgroundColor: SERIES[1],
      borderRadius: 4,
      maxBarThickness: 22,
    }],
  };

  return (
    <div className="grid" style={{ gap: 14 }}>
      <div className="grid kpi-grid">
        <KpiCard label="Sales per Labor Hour" value={money(fleetSplh)} sub="all locations with punch data" />
        <KpiCard label="Labor Hours" value={int(Math.round(totalHours))} sub={`${int(totalShifts)} shifts`} />
        <KpiCard label="Net Sales (covered)" value={money(totalSales)} sub="locations with punch data" />
        <KpiCard label="Avg Shift Length" value={totalShifts ? `${(totalHours / totalShifts).toFixed(1)} h` : '—'} />
      </div>

      <Card
        tour="labor-bar"
        title="Sales per Labor Hour by Location"
        sub={`${niceDate(startDate)} – ${niceDate(endDate)} · higher = more productive`}
      >
        <div className="chart-box tall">
          <Bar
            data={barData}
            options={{
              indexAxis: 'y',
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                legend: { display: false },
                tooltip: {
                  callbacks: {
                    label: ctx => {
                      const r = sorted[ctx.dataIndex];
                      return ` ${money(r.salesPerLaborHour)}/h · ${int(Math.round(r.laborHours))} hrs · ${money(r.netSales)} sales`;
                    },
                  },
                },
              },
              scales: {
                x: { ticks: { callback: v => money(v, { cents: false }) }, beginAtZero: true },
                y: { grid: { display: false }, ticks: { autoSkip: false } },
              },
            }}
          />
        </div>
      </Card>

      <Card tour="labor-detail" title="Labor Detail" sub="shift punches per location">
        <DataTable
          csvName="labor-by-location.csv"
          defaultSort={{ key: 'salesPerLaborHour', dir: 'desc' }}
          columns={[
            { key: 'locationName', label: 'Location' },
            { key: 'laborHours', label: 'Labor Hrs', num: true, render: r => int(Math.round(r.laborHours)), csv: r => r.laborHours },
            { key: 'shifts', label: 'Shifts', num: true, render: r => int(r.shifts), csv: r => r.shifts },
            {
              key: 'avgShift', label: 'Avg Shift', num: true,
              sortValue: r => (r.shifts ? r.laborHours / r.shifts : 0),
              render: r => r.shifts ? `${(r.laborHours / r.shifts).toFixed(1)} h` : '—',
              csv: r => r.shifts ? (r.laborHours / r.shifts).toFixed(2) : '',
            },
            { key: 'netSales', label: 'Net Sales', num: true, render: r => money(r.netSales), csv: r => (r.netSales / 100).toFixed(2) },
            { key: 'orders', label: 'Orders', num: true, render: r => int(r.orders), csv: r => r.orders },
            {
              key: 'ordersPerHour', label: 'Orders / Hr', num: true,
              sortValue: r => (r.laborHours ? r.orders / r.laborHours : 0),
              render: r => r.laborHours ? (r.orders / r.laborHours).toFixed(1) : '—',
              csv: r => r.laborHours ? (r.orders / r.laborHours).toFixed(2) : '',
            },
            { key: 'salesPerLaborHour', label: '$ / Labor Hr', num: true, render: r => money(r.salesPerLaborHour), csv: r => (r.salesPerLaborHour / 100).toFixed(2) },
          ]}
          rows={withLabor.map(r => ({ ...r, __key: r.locationId }))}
        />
        <div className="legend-note">
          Wage cost isn't shown because hourly-wage data in the POS is unreliable for several
          locations — hours and sales-per-labor-hour are computed from raw shift punches only.
        </div>
      </Card>
    </div>
  );
}
